import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { publicAPI } from '../lib/api';
import { cn, formatDate } from '../lib/utils';
import { Trophy, Users, Crown, Clock, User, Phone, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_3ec18717-9700-48f1-a5a2-71ac0edd3a6f/artifacts/29waugc4_WhatsApp%20Image%202026-03-06%20at%2016.26.44.jpeg';

export default function LiveTournament() {
  const { tournamentId } = useParams();
  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [pointsTable, setPointsTable] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('matches');
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchData = useCallback(async () => {
    try {
      const [tRes, mRes, ptRes] = await Promise.all([
        publicAPI.getTournament(tournamentId),
        publicAPI.getTournamentMatches(tournamentId),
        publicAPI.getPointsTable(tournamentId).catch(() => ({ data: {} })),
      ]);
      setTournament(tRes.data);
      setMatches(mRes.data || []);
      setPointsTable(ptRes.data || {});
      setLastRefresh(new Date());
    } catch (err) {
      // Public tournament fetch failed; the UI will show a "Tournament not found" empty state.
      if (process.env.NODE_ENV !== 'production') console.debug('live tournament fetch failed:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 8 seconds so spectators track live scores in near-real-time
  useEffect(() => {
    const interval = setInterval(fetchData, 8000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const teamsMap = {};
  if (tournament?.teams) tournament.teams.forEach(t => { teamsMap[t.team_id] = t; });
  const getTeamName = (teamId) => teamsMap[teamId]?.team_name || teamId;

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-4">
        <Skeleton className="h-12 w-48 mx-auto mb-6" />
        <Skeleton className="h-[400px] max-w-4xl mx-auto" />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card><CardContent className="p-12 text-center"><Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground" /><p className="text-muted-foreground">Tournament not found</p></CardContent></Card>
      </div>
    );
  }

  const poolMatches = matches.filter(m => m.stage === 'pool');
  const knockoutMatches = matches.filter(m => m.stage === 'knockout');
  const liveMatches = matches.filter(m => m.status === 'live');
  // Live first, pending next (in play order), completed at the bottom.
  const statusRank = { live: 0, pending: 1, completed: 2 };
  const sortByPlayOrder = (arr) => [...arr].sort((a, b) =>
    (statusRank[a.status] ?? 1) - (statusRank[b.status] ?? 1) ||
    ((a.play_order ?? a.match_number ?? 0) - (b.play_order ?? b.match_number ?? 0))
  );

  return (
    <div className="min-h-screen bg-background" data-testid="live-tournament-page">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="LoveAll" className="h-8 w-8 rounded-md object-cover" />
              <span className="font-heading text-lg font-bold uppercase tracking-tight">LoveAll</span>
            </div>
            <Badge variant="outline" className="text-xs">LIVE</Badge>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Tournament Info */}
        <div className="text-center">
          <h1 className="font-heading text-2xl sm:text-4xl font-bold uppercase tracking-tight" data-testid="live-tournament-name">{tournament.name}</h1>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-2 text-sm text-muted-foreground">
            <Badge className={cn(
              tournament.status === 'registration' && 'bg-blue-500',
              tournament.status === 'in_progress' && 'bg-green-500',
              tournament.status === 'completed' && 'bg-gray-500',
            )}>{tournament.status.replace('_', ' ')}</Badge>
            <span className="capitalize">{tournament.format.replace('_', ' ')} / {tournament.match_type}</span>
            {tournament.organizer_name && <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{tournament.organizer_name}</span>}
            {tournament.start_date && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{formatDate(tournament.start_date)}</span>}
          </div>
        </div>

        {/* Live matches banner */}
        {liveMatches.length > 0 && (
          <Card className="border-red-500/30 bg-red-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                <span className="font-heading text-sm font-bold uppercase">Live Matches</span>
              </div>
              <div className="space-y-2">
                {liveMatches.map(match => (
                  <div key={match.match_id} className="flex items-center justify-between p-3 rounded-lg bg-background/50">
                    <div>
                      <span className="font-bold">{getTeamName(match.team1_id)}</span>
                      <span className="text-muted-foreground mx-2 font-mono text-xs">vs</span>
                      <span className="font-bold">{getTeamName(match.team2_id)}</span>
                    </div>
                    {match.scores?.length > 0 && (
                      <div className="flex gap-2">
                        {match.scores.map((g, i) => (
                          <span key={`live-${match.match_id}-g${i}`} className="font-mono text-sm bg-muted px-2 py-0.5 rounded">{g.team1_score}-{g.team2_score}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Auto-refresh indicator */}
        <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
          <RefreshCw className="h-3 w-3" />
          <span>Auto-refreshes every 8s</span>
          <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={fetchData}>Refresh now</Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start" data-testid="live-tabs">
            <TabsTrigger value="matches">Matches</TabsTrigger>
            <TabsTrigger value="points">Points Table</TabsTrigger>
            {knockoutMatches.length > 0 && <TabsTrigger value="knockout">Knockout</TabsTrigger>}
            <TabsTrigger value="teams">Teams</TabsTrigger>
          </TabsList>

          {/* MATCHES */}
          <TabsContent value="matches" className="mt-6 space-y-6">
            {tournament.pools?.map(pool => {
              const pMatches = sortByPlayOrder(poolMatches.filter(m => m.pool_name === pool.pool_name));
              return (
                <div key={pool.pool_name}>
                  <h3 className="font-heading text-lg font-bold mb-3">Pool {pool.pool_name}</h3>
                  <div className="space-y-2">
                    {pMatches.map(match => (
                      <PublicMatchCard key={match.match_id} match={match} getTeamName={getTeamName} />
                    ))}
                  </div>
                </div>
              );
            })}
            {knockoutMatches.length > 0 && (
              <div>
                <h3 className="font-heading text-lg font-bold mb-3 flex items-center gap-2"><Crown className="h-5 w-5 text-accent" />Knockout</h3>
                <div className="space-y-2">
                  {sortByPlayOrder(knockoutMatches).map(match => (
                    <PublicMatchCard key={match.match_id} match={match} getTeamName={getTeamName} />
                  ))}
                </div>
              </div>
            )}
            {matches.length === 0 && <Card><CardContent className="py-12 text-center text-muted-foreground">No matches yet</CardContent></Card>}
          </TabsContent>

          {/* POINTS TABLE */}
          <TabsContent value="points" className="mt-6 space-y-6">
            {Object.entries(pointsTable).map(([poolName, standings]) => (
              <div key={poolName}>
                <h3 className="font-heading text-lg font-bold mb-3">Pool {poolName}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid={`live-points-table-${poolName}`}>
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left p-2">#</th>
                        <th className="text-left p-2">Team</th>
                        <th className="text-center p-2">P</th>
                        <th className="text-center p-2">W</th>
                        <th className="text-center p-2">L</th>
                        <th className="text-center p-2">PF</th>
                        <th className="text-center p-2">PA</th>
                        <th className="text-center p-2">Diff</th>
                        <th className="text-center p-2 font-bold">Pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standings.map((row, idx) => (
                        <tr key={row.team_id} className={cn('border-b', idx < 2 && 'bg-accent/5')}>
                          <td className="p-2 font-mono">{idx + 1}</td>
                          <td className="p-2 font-medium">{row.team_name}</td>
                          <td className="p-2 text-center font-mono">{row.played}</td>
                          <td className="p-2 text-center font-mono text-green-500">{row.won}</td>
                          <td className="p-2 text-center font-mono text-red-400">{row.lost}</td>
                          <td className="p-2 text-center font-mono">{row.points_scored}</td>
                          <td className="p-2 text-center font-mono">{row.points_conceded}</td>
                          <td className="p-2 text-center font-mono">{row.score_diff > 0 ? '+' : ''}{row.score_diff}</td>
                          <td className="p-2 text-center font-mono font-bold">{row.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {Object.keys(pointsTable).length === 0 && <Card><CardContent className="py-12 text-center text-muted-foreground">Points table will appear once matches are played</CardContent></Card>}
          </TabsContent>

          {/* KNOCKOUT */}
          <TabsContent value="knockout" className="mt-6 space-y-6">
            {tournament.knockout?.rounds?.map((round, rIdx) => {
              const roundMatches = knockoutMatches.filter(m => m.round === round.round);
              const isFinal = round.round === 'Final';
              const finalWinner = isFinal && roundMatches[0]?.status === 'completed' ? roundMatches[0]?.winner_id : null;
              return (
                <div key={`ko-${round.round}-${rIdx}`}>
                  <h3 className="font-heading text-lg font-bold mb-3">{round.round}</h3>
                  <div className="space-y-2">
                    {roundMatches.map(match => (
                      <PublicMatchCard key={match.match_id} match={match} getTeamName={getTeamName} />
                    ))}
                  </div>
                  {finalWinner && (
                    <Card className="mt-4 border-accent/50 bg-accent/5">
                      <CardContent className="p-6 text-center">
                        <Crown className="h-10 w-10 text-accent mx-auto mb-2" />
                        <div className="font-heading text-xl font-bold uppercase">Champion</div>
                        <div className="text-lg mt-1">{getTeamName(finalWinner)}</div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              );
            })}
          </TabsContent>

          {/* TEAMS */}
          <TabsContent value="teams" className="mt-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {tournament.teams?.map((team, idx) => (
                <Card key={team.team_id}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold">{idx + 1}</div>
                    <div>
                      <div className="font-medium">{team.team_name}</div>
                      <div className="text-xs text-muted-foreground">{team.player1_name}{team.player2_name ? ` & ${team.player2_name}` : ''}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {!tournament.teams?.length && <Card><CardContent className="py-12 text-center text-muted-foreground">No teams registered yet</CardContent></Card>}
          </TabsContent>
        </Tabs>

        {/* Footer with share */}
        <div className="text-center space-y-3 pt-8 pb-4">
          <div className="flex justify-center gap-2">
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => {
              const text = encodeURIComponent(`${tournament.name} - Live Scores\nFollow live: ${window.location.href}`);
              window.open(`https://wa.me/?text=${text}`, '_blank');
            }} data-testid="live-whatsapp-share-btn">
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Share on WhatsApp
            </Button>
            <Button size="sm" variant="outline" onClick={async () => {
              try { await navigator.clipboard.writeText(window.location.href); }
              catch (err) {
                if (process.env.NODE_ENV !== 'production') console.debug('clipboard copy failed:', err?.message);
              }
            }} data-testid="live-copy-link-btn">
              Copy Link
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">
            Powered by <span className="font-heading font-bold">LoveAll</span> Badminton
          </div>
        </div>
      </div>
    </div>
  );
}

function PublicMatchCard({ match, getTeamName }) {
  const statusColors = { pending: 'bg-gray-500', live: 'bg-red-500 animate-pulse', completed: 'bg-green-500' };
  return (
    <Card className={cn('transition-all', match.status === 'live' && 'border-red-500/30')} data-testid={`live-match-${match.match_id}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {match.match_number && (
                <span className="text-[10px] font-mono font-bold border border-border rounded px-1.5 py-0.5 text-muted-foreground">#{match.match_number}</span>
              )}
              <Badge className={cn('text-[10px]', statusColors[match.status])}>{match.status}</Badge>
              {match.pool_name && <span className="text-xs text-muted-foreground">Pool {match.pool_name}</span>}
              {match.round && <span className="text-xs text-muted-foreground">{match.round}</span>}
              {match.walkover && <Badge variant="outline" className="text-[10px] text-amber-500 border-amber-500/40">Walkover</Badge>}
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className={cn('font-bold', match.winner_id === match.team1_id && 'text-green-500')}>{getTeamName(match.team1_id)}</span>
              <span className="text-muted-foreground font-mono text-xs">vs</span>
              <span className={cn('font-bold', match.winner_id === match.team2_id && 'text-green-500')}>{getTeamName(match.team2_id)}</span>
            </div>
          </div>
          {match.walkover ? (
            <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded">0-0 (Walkover)</span>
          ) : match.scores?.length > 0 && (
            <div className="flex gap-2">
              {match.scores.map((g, i) => (
                <span key={`pmc-${match.match_id}-g${i}`} className="font-mono text-sm bg-muted px-2 py-0.5 rounded">{g.team1_score}-{g.team2_score}</span>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
